import { useState } from 'react';
import { deliveryAuth } from '../services/auth';
import toast from 'react-hot-toast';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: any) => void;
}

const AuthModal = ({ isOpen, onClose, onSuccess }: AuthModalProps) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'guest'>('login');
  const [formData, setFormData] = useState({
    phone: '',
    name: '',
    email: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogin = async () => {
    if (!formData.phone || formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const result = await deliveryAuth.login(formData.phone);
      if (result.success) {
        toast.success('Welcome back!');
        onSuccess(result.user);
        onClose();
      } else {
        toast.error(result.error || 'Failed');
      }
    } catch (error) {
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!formData.phone || formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter your address');
      return;
    }

    setLoading(true);
    try {
      const result = await deliveryAuth.signUp(formData);
      if (result.success) {
        toast.success('Account created successfully!');
        onSuccess(result.user);
        onClose();
      } else {
        toast.error(result.error || 'Failed');
      }
    } catch (error) {
      toast.error('Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGuest = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.phone || formData.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter your address');
      return;
    }

    const result = deliveryAuth.continueAsGuest(formData);
    if (result.success) {
      toast.success('Continuing as guest');
      onSuccess(result.user);
      onClose();
    }
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modal: {
      background: 'white',
      borderRadius: '12px',
      padding: '32px',
      maxWidth: '400px',
      width: '90%',
      maxHeight: '90vh',
      overflowY: 'auto' as const
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      marginBottom: '8px',
      textAlign: 'center' as const
    },
    subtitle: {
      color: '#6b7280',
      textAlign: 'center' as const,
      marginBottom: '24px'
    },
    input: {
      width: '100%',
      padding: '12px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      marginBottom: '16px'
    },
    button: {
      width: '100%',
      padding: '12px',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      marginBottom: '12px',
      transition: 'all 0.2s'
    },
    primaryButton: {
      background: '#f97316',
      color: 'white'
    },
    secondaryButton: {
      background: '#f3f4f6',
      color: '#374151'
    },
    linkButton: {
      background: 'none',
      border: 'none',
      color: '#f97316',
      cursor: 'pointer',
      textDecoration: 'underline',
      fontSize: '14px'
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {mode === 'login' && (
          <>
            <h2 style={styles.title}>Welcome Back! 👋</h2>
            <p style={styles.subtitle}>Login with your phone number</p>
            
            <input
              type="tel"
              placeholder="Phone Number (10 digits)"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              style={styles.input}
              maxLength={10}
            />

            <button
              onClick={handleLogin}
              disabled={loading}
              style={{...styles.button, ...styles.primaryButton}}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>

            <button
              onClick={() => setMode('signup')}
              style={{...styles.button, ...styles.secondaryButton}}
            >
              Create New Account
            </button>

            <button
              onClick={() => setMode('guest')}
              style={{...styles.button, ...styles.secondaryButton}}
            >
              Continue as Guest
            </button>
          </>
        )}

        {mode === 'signup' && (
          <>
            <h2 style={styles.title}>Create Account 🚀</h2>
            <p style={styles.subtitle}>Join us for faster ordering</p>
            
            <input
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              style={styles.input}
            />

            <input
              type="tel"
              placeholder="Phone Number (10 digits)"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              style={styles.input}
              maxLength={10}
            />

            <input
              type="email"
              placeholder="Email (Optional)"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              style={styles.input}
            />

            <textarea
              placeholder="Delivery Address"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              style={{...styles.input, minHeight: '80px', resize: 'vertical' as const}}
            />

            <button
              onClick={handleSignup}
              disabled={loading}
              style={{...styles.button, ...styles.primaryButton}}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            <button
              onClick={() => setMode('login')}
              style={styles.linkButton}
            >
              Already have an account? Login
            </button>
          </>
        )}

        {mode === 'guest' && (
          <>
            <h2 style={styles.title}>Guest Checkout 🛒</h2>
            <p style={styles.subtitle}>Order without creating an account</p>
            
            <input
              type="text"
              placeholder="Full Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              style={styles.input}
            />

            <input
              type="tel"
              placeholder="Phone Number (10 digits)"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              style={styles.input}
              maxLength={10}
            />

            <input
              type="email"
              placeholder="Email (Optional)"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              style={styles.input}
            />

            <textarea
              placeholder="Delivery Address"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              style={{...styles.input, minHeight: '80px', resize: 'vertical' as const}}
            />

            <button
              onClick={handleGuest}
              style={{...styles.button, ...styles.primaryButton}}
            >
              Continue as Guest
            </button>

            <button
              onClick={() => setMode('signup')}
              style={styles.linkButton}
            >
              Want to create an account instead?
            </button>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button
            onClick={onClose}
            style={styles.linkButton}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;